import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';
import { parse } from 'query-string';
import { deepEqual } from 'fast-equals';
import { useSelector } from 'react-redux';

import { NON_SEARCH_PREFIXES, NULL_SEARCH_RE, OOS_REDIRECTED_SEARCH_RE, PRODUCT_ASIN, TERMINAL_ZSO_PATH_RE, ZSO_URL_RE } from 'common/regex';
// actions
import { setError } from 'actions/errors';
import { redirectWithAppRoot } from 'actions/redirect';
import usePrevious from 'hooks/usePrevious';
import usePlpScrollRestoration from 'hooks/usePlpScrollRestoration';
import { fetchFromSearch, fetchFromZso, processReceivedSearchResponse } from 'actions/fancyUrls';
import NoSearchResults from 'components/search/NoSearchResults';
import useMartyContext from 'hooks/useMartyContext';
// resources
import { productBundle } from 'apis/cloudcatalog';
import { logDebug } from 'middleware/logger';
import { trackEvent, trackLegacyEvent } from 'helpers/analytics';
import { FetchError, fetchErrorMiddleware } from 'middleware/fetchErrorMiddleware';
import { processHeadersMiddleware } from 'middleware/processHeadersMiddlewareFactory';
import { setSessionCookies } from 'actions/session';
import ProductUtils from 'helpers/ProductUtils';
import { isDesktop } from 'helpers/ClientUtils';
import {
  isPrettySearchOrSlashSearchPath,
  normalizeSearchLocation,
  shouldSearchServiceBeCalled,
  shouldUpdateClientSearchUrl
} from 'helpers/SearchUtils';
import { evSearchPageView } from 'events/search';
import { trackAttiEvent, trackOperation } from 'events/ops/opsEvent';
import { PAGE_TYPES } from 'constants/appConstants';
import { selectHolmes } from 'selectors/holmes';
import { selectRewardsDetermined, selectRewardsInfo } from 'selectors/rewards';
import { consumeActionTimestamp, recordActionTimestamp } from 'helpers/attiTimestamps';

const SearchLogic = props => {
  // marketplace
  const {
    router,
    marketplace: {
      search: { oosMessaging, useAutoCorrect },
      checkout: { allowMoveToFavorites },
      hasBannerAds,
      is6pm
    }
  } = useMartyContext();

  const {
    params: { seoName }
  } = useRouteMatch();

  const {
    children,
    location,
    setHFSearchTerm,
    filters,
    setOosMessaging,
    getHearts,
    pageTypeChange,
    fetchSymphonySearchComponents,
    fetchLandingPageInfo,
    setUrlUpdated,
    filters: { autocorrect, executedSearchUrl: filtersExecutedSearchUrl, term, originalTerm, staleProducts, shouldUrlUpdate },
    products: {
      executedSearchUrl,
      list,
      isLoading,
      inlineRecos,
      recommendations,
      isBlacklisted,
      requestedUrl,
      oosMessaging: productOosMessaging,
      totalProductCount,
      searchError
    },
    fetchFromSearch,
    fetchFromZso,
    isVip,
    landingPage,
    facets,
    fireSearchPixels,
    pixelFacetData
  } = props;

  const holmes = useSelector(selectHolmes);
  const { directedId, customerId, isHolmesDetermined } = holmes ?? {};

  const rewards = useSelector(selectRewardsInfo);
  const isRewardsDetermined = useSelector(selectRewardsDetermined);
  const { isVipOrConsented } = rewards;

  // Keep the clicked product card in view on the PDP → Back → PLP flow while the
  // page reflows late content in above the grid.
  usePlpScrollRestoration();

  // Search loaded
  const [isLoaded, setIsLoaded] = useState(false);
  const prevLocation = usePrevious(location);
  const prevStale = usePrevious(staleProducts);

  // Ops event tracking refs
  const isFilterSortRef = useRef(false);
  const hasTrackedSearchRef = useRef(null);

  // Holds the controller for the most recent in-flight search request.
  // Aborted on unmount (navigation away) and when a new search supersedes it.
  const abortControllerRef = useRef(null);

  const requestSearchResultsForLocation = useCallback(
    ({ location, isFresh, bypassCache, shouldAppendResults, isFilterSort, signal }) => {
      if (ZSO_URL_RE.test(location.pathname)) {
        return fetchFromZso({
          location,
          isFresh,
          bypassCache,
          shouldAppendResults,
          isFilterSort,
          signal
        });
        // this should not run on PDP forward transtion
      } else if (!location.pathname.includes('/p/')) {
        return fetchFromSearch({
          location: normalizeSearchLocation(location),
          isFresh,
          bypassCache,
          shouldAppendResults,
          isFilterSort,
          signal
        });
      }
    },
    [fetchFromSearch, fetchFromZso]
  );

  const hasProductResults = () => !!list?.length;

  // While the primary search is degraded we render the loading state (a retry is
  // pending), not the no-results state — so suppress NoSearchResults here.
  const shouldShowNoResults = () => !!requestedUrl && !isLoading && !searchError && !hasProductResults();

  const getPageFromUrlSearch = search => {
    const nextUrlSearchParsed = parse(search);
    return nextUrlSearchParsed?.p && parseInt(nextUrlSearchParsed.p, 10);
  };

  const nextUrlSearchPageParam = getPageFromUrlSearch(location.search);

  const mobileScrollToTop = useCallback(() => !isDesktop() && window.scrollTo(0, 0), []);

  useEffect(() => {
    if (originalTerm || autocorrect?.termBeforeAutocorrect) {
      // keep the header term in sync with the searched for term.
      const autoCorrectTerm = autocorrect?.termBeforeAutocorrect;
      setHFSearchTerm((useAutoCorrect && autoCorrectTerm) || originalTerm); // For Marty HF
    }

    if (originalTerm || (!isLoaded && filtersExecutedSearchUrl)) {
      setIsLoaded(true);
    }
  }, [originalTerm, filtersExecutedSearchUrl, isLoaded, setHFSearchTerm, useAutoCorrect, autocorrect?.termBeforeAutocorrect]);

  useEffect(() => {
    if (allowMoveToFavorites) {
      getHearts();
    }
  }, [list, recommendations, allowMoveToFavorites, getHearts, inlineRecos]);

  useEffect(() => {
    if (hasBannerAds) {
      fetchSymphonySearchComponents();
    }
  }, [hasBannerAds, filtersExecutedSearchUrl, fetchSymphonySearchComponents]);

  // Fire the page view for a server-rendered PLP entry (direct/homepage/external). filtersExecutedSearchUrl
  // is set only when the search resolved server-side, so this won't double-fire with the client fetch path
  // (fancyUrls) that handles PLP→PLP and filter/sort/paginate. Empty deps: once per fresh mount.
  useEffect(() => {
    if (filtersExecutedSearchUrl) {
      evSearchPageView(props);
    }
  }, []);

  // componentDidMount
  useEffect(() => {
    pageTypeChange(PAGE_TYPES.SEARCH_PAGE);
  }, [pageTypeChange]);

  // Abort the in-flight search only on unmount (navigation away from search)
  // so a cancelled request never resolves into a false ErrorPage.
  useEffect(() => () => abortControllerRef.current?.abort(), []);

  // Client-only (post-hydration) retry trigger. Mount effects do not run during SSR
  // (renderToString does not mount effects), so this fires only after hydration. When the page was
  // server-rendered into the loading state (primary search failed, no results yet),
  // re-issue the primary search request from the client. The fetch action then retries internally
  // with backoff and renders the full-page error if it ultimately fails.
  useEffect(() => {
    if (searchError && !hasProductResults()) {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;
      requestSearchResultsForLocation({ location, bypassCache: true, signal: controller.signal });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only for 6pm & ZSO urls should we update the browser url with the executedSearchUrl
    if (shouldUpdateClientSearchUrl({ is6pm, requestedUrl, executedSearchUrl })) {
      window.history.replaceState(null, '', executedSearchUrl);
    }
    // This entire effect is a massive antipattern
    // The URL should be entirely dictating the state of the page, rather than
    if (shouldUrlUpdate) {
      const isTermSearch = !!(
        requestedUrl &&
        (NULL_SEARCH_RE.test(requestedUrl) || OOS_REDIRECTED_SEARCH_RE.test(requestedUrl)) &&
        (term || originalTerm)
      );
      if (isTermSearch) {
        setUrlUpdated();
        // no nothing
      } else if (
        filtersExecutedSearchUrl &&
        (!nextUrlSearchPageParam || prevStale) &&
        (!location.action || (!!location.action && location.action === 'PUSH') || !!prevLocation || !!location.search.length || !term)
      ) {
        logDebug(`pushing browser url to ${executedSearchUrl}`);
        // Order matters here to prevent a double push to the same URL
        setUrlUpdated();
        window.history.replaceState(null, '', executedSearchUrl);
      }
    }
  }, [requestedUrl, executedSearchUrl, router, prevStale, shouldUrlUpdate, location, setUrlUpdated, term, originalTerm, filtersExecutedSearchUrl]);

  useEffect(() => {
    if (!isBlacklisted && shouldSearchServiceBeCalled(location, seoName, executedSearchUrl) && !deepEqual(location, prevLocation)) {
      // breadcrumb/page update
      logDebug('calling products due to breadcrumb or page update');

      // Abort any in-flight search before starting a new one (supersede).
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      recordActionTimestamp('SearchSubmitToResults');
      requestSearchResultsForLocation({ location, signal: controller.signal });
      mobileScrollToTop();

      // Cancel in-flight search on re-run (new search supersedes) or unmount (navigation away)
      // return () => controller.abort();
    }
  }, [location, requestSearchResultsForLocation, prevLocation, isBlacklisted, mobileScrollToTop, executedSearchUrl, seoName]);

  useEffect(() => {
    if (!isBlacklisted && staleProducts && staleProducts !== prevStale) {
      // facet/sort update
      logDebug('stale products due to facet or sort update');

      // Abort any in-flight search before starting a new one (supersede).
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      isFilterSortRef.current = true;
      recordActionTimestamp('FilterSortApply');
      const { facetUrlQueryParams, facetUrlPath } = facets;
      const mockLocation = { pathname: facetUrlPath, search: facetUrlQueryParams ? `?${facetUrlQueryParams}` : '' };
      requestSearchResultsForLocation({ location: mockLocation, isFilterSort: true, signal: controller.signal });
      mobileScrollToTop();

      // Cancel in-flight search on re-run (new search supersedes) or unmount (navigation away)
      // return () => controller.abort();
    }
  }, [
    requestedUrl,
    router,
    location,
    staleProducts,
    requestSearchResultsForLocation,
    setOosMessaging,
    filters,
    isBlacklisted,
    mobileScrollToTop,
    prevLocation,
    prevStale
  ]);

  // Emit the success ops event + ATTI when products finish loading. Failure metrics for a degraded
  // primary search are emitted at the request source (reportDegradation, exactly one per request),
  // and the retry coordinator owns the terminal full-page error after the retry limit is exhausted,
  // so failures are no longer tracked here.
  useEffect(() => {
    if (isLoading) return;

    if (!executedSearchUrl) return;

    // One emission per executedSearchUrl
    if (hasTrackedSearchRef.current === executedSearchUrl) return;
    hasTrackedSearchRef.current = executedSearchUrl;

    const operation = isFilterSortRef.current ? 'FilterSortApplication' : 'SearchResultsLoad';
    trackOperation(operation, 'success');

    // ATTI emission — consume the recorded timestamp if present
    const attiExperience = isFilterSortRef.current ? 'FilterSortApply' : 'SearchSubmitToResults';
    const actionTs = consumeActionTimestamp(attiExperience);
    if (actionTs) {
      trackAttiEvent('Search', attiExperience, actionTs, Date.now());
    }

    isFilterSortRef.current = false;
  }, [isLoading, list, executedSearchUrl]);

  useEffect(() => {
    // if they we redirected to search due to an oos item
    const oosRedirected = new URLSearchParams(location.search).get('oosRedirected');
    if (!productOosMessaging && oosRedirected) {
      setOosMessaging(oosMessaging);
    } else if (productOosMessaging && !oosRedirected) {
      setOosMessaging(null);
    }
    // Empty array ensures that oosMessaging is updated only once regardless of fancy url update
  }, [productOosMessaging]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isHolmesDetermined || !isRewardsDetermined) {
      return;
    }

    fireSearchPixels({
      term,
      results: list,
      pixelFacetData,
      filters,
      searchResultsCount: totalProductCount,
      directedId,
      customerId,
      isVip: isVipOrConsented,
      pageNumber: nextUrlSearchPageParam
    });
    // fire when user is identified and the page changes
  }, [isHolmesDetermined, nextUrlSearchPageParam, isRewardsDetermined]);

  return (
    <>
      {shouldShowNoResults() && (
        <NoSearchResults filters={filters} landingPage={landingPage} isVip={isVip} fetchLandingPageInfo={fetchLandingPageInfo} />
      )}
      {children({
        hasProductResults,
        shouldShowNoResults
      })}
    </>
  );
};

SearchLogic.build404ErrorMessage = () => setError('404 not found', new Error('Not a Search URL.'), 404);

SearchLogic.fetchDataOnServer = (
  store,
  location,
  params,
  { zsoFetch = fetchFromZso, searchFetch = fetchFromSearch, redirect = redirectWithAppRoot, asinFetch = productBundle } = {}
) => {
  const { dispatch, getState } = store;
  const qs = location.pathname === '/search' ? parse(location.search) : {};
  const potentialAsin = (params.seoName || qs.term || '').toUpperCase();

  if (ZSO_URL_RE.test(location.pathname)) {
    if (NON_SEARCH_PREFIXES.test(location.pathname) || !TERMINAL_ZSO_PATH_RE.test(location.pathname)) {
      return dispatch(SearchLogic.build404ErrorMessage());
    } else {
      return dispatch(zsoFetch({ location, keepParams: true }));
    }
  } else if (PRODUCT_ASIN.test(potentialAsin)) {
    const state = getState();
    const {
      environmentConfig: {
        api: { cloudcatalog: cloudcatalogInfo }
      }
    } = state;
    return asinFetch(cloudcatalogInfo, { asin: potentialAsin })
      .then(processHeadersMiddleware(setSessionCookies(dispatch, getState, true))) // will skip setting cookies if there are missing cookies
      .then(fetchErrorMiddleware)
      .then(response => {
        const productReceived = response.statusCode === '200' && response.product.length;
        if (productReceived) {
          const productUrl = ProductUtils.getProductUrlFromAsin(response.product[0], potentialAsin);
          if (productUrl) {
            return dispatch(redirect(productUrl));
          }
        }
        throw new FetchError(location.pathname, response.statusCode, `Unexpected CloudCatalogAPI response for ASIN: ${potentialAsin}`);
      })
      .catch(() => {
        if (isPrettySearchOrSlashSearchPath(location.pathname)) {
          return dispatch(
            searchFetch({
              location: normalizeSearchLocation(location),
              keepParams: true
            })
          );
        } else {
          return dispatch(SearchLogic.build404ErrorMessage());
        }
      });
  } else if (isPrettySearchOrSlashSearchPath(location.pathname)) {
    return dispatch(
      searchFetch({
        location: normalizeSearchLocation(location),
        keepParams: true,
        isSearchHappeningServerSide: true
      })
    );
  } else {
    return dispatch(SearchLogic.build404ErrorMessage());
  }
};

SearchLogic.afterFetchDataOnServer = ({ dispatch, getState, doProcessReceivedSearchResponse = processReceivedSearchResponse }) => {
  const {
    products: { deferredSearchResponse }
  } = getState();
  if (!deferredSearchResponse) {
    return;
  }
  const { response, parsedParams, completeUrl } = deferredSearchResponse;

  doProcessReceivedSearchResponse(response, dispatch, getState, parsedParams, completeUrl);
};

SearchLogic.defaultProps = {
  trackEvent,
  trackLegacyEvent
};

SearchLogic.propTypes = {
  products: PropTypes.object.isRequired,
  filters: PropTypes.object,
  facets: PropTypes.object,
  setOosMessaging: PropTypes.func
};

export default SearchLogic;
